import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Tab,
  Tabs,
  CircularProgress,
  Typography,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { RefreshOutlined } from '@mui/icons-material';
import { useEditorCommunication } from '@hooks/useEditorCommunication';
import { useRunStageStore } from '@/run-stage/store/runStageStore';
import { RunHeader } from './RunHeader';
import { PlanStage } from './PlanStage/PlanStage';
import { ExecuteStage } from './ExecuteStage/ExecuteStage';

const COMM_CONFIG = {
  targetOrigin: '*',
  debug: false,
};

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`run-tabpanel-${index}`}
    aria-labelledby={`run-tab-${index}`}
    sx={{ flex: 1, overflow: 'auto', display: value === index ? 'flex' : 'none', flexDirection: 'column' }}
  >
    {value === index && children}
  </Box>
);

export const RunInfoPage: React.FC = () => {
  const communication = useEditorCommunication(COMM_CONFIG);
  const [activeTab, setActiveTab] = useState(0);

  const status = useRunStageStore((s) => s.status);
  const context = useRunStageStore((s) => s.context);
  const uiPermissions = useRunStageStore((s) => s.uiPermissions);
  const errorMessage = useRunStageStore((s) => s.error);
  const hydrate = useRunStageStore((s) => s.hydrate);
  const refresh = useRunStageStore((s) => s.refresh);
  const fetchUIPermissions = useRunStageStore((s) => s.fetchUIPermissions);
  const applyPauseRun = useRunStageStore((s) => s.applyPauseRun);
  const applyResumeRun = useRunStageStore((s) => s.applyResumeRun);
  const applyAbortRun = useRunStageStore((s) => s.applyAbortRun);

  const runId = useMemo(
    () => communication.initPayload?.entityId ?? '',
    [communication.initPayload?.entityId]
  );

  useEffect(() => {
    if (!runId) return;

    hydrate(runId).catch((err: Error) => {
      console.error('Failed to load run stage context', err);
      communication.sendError(
        'RUN_STAGE_INIT_FAILED',
        err.message || 'Failed to load run stage context',
        err
      );
    });
  }, [runId, hydrate, communication.sendError]);

  useEffect(() => {
    if (!runId || status !== 'ready') return;
    fetchUIPermissions(runId);
  }, [runId, status, fetchUIPermissions]);

  const handleRefresh = async () => {
    try {
      await refresh();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to refresh run stage context';
      communication.sendError('RUN_STAGE_REFRESH_FAILED', message, err);
    }
  };

  const handlePause = async () => {
    try {
      await applyPauseRun();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to pause run';
      communication.sendError('RUN_STAGE_PAUSE_FAILED', message, err);
    }
  };

  const handleResume = async () => {
    try {
      await applyResumeRun();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to resume run';
      communication.sendError('RUN_STAGE_RESUME_FAILED', message, err);
    }
  };

  const handleAbort = async () => {
    try {
      await applyAbortRun();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to abort run';
      communication.sendError('RUN_STAGE_ABORT_FAILED', message, err);
    }
  };

  if (!communication.isReady || status === 'idle' || (status === 'loading' && !context)) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress size={36} />
        <Typography variant="body2" color="text.secondary">
          Loading run stage…
        </Typography>
      </Box>
    );
  }

  if (status === 'error' && !context) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{'Failed to load run stage'}</Alert>
      </Box>
    );
  }

  if (!context) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">No run stage context available</Alert>
      </Box>
    );
  }

  const isSyncing = status === 'loading';
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.default',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <RunHeader
        run={context.run}
        uiPermissions={uiPermissions ?? undefined}
        isSyncing={isSyncing}
        onPause={handlePause}
        onResume={handleResume}
        onAbort={handleAbort}
      />

      {/* Global error banner (non-fatal) */}
      {errorMessage && (
        <Alert severity="warning" sx={{ mx: 2, mt: 1 }} onClose={() => {}}>
          {errorMessage}
        </Alert>
      )}

      {/* Tabs */}
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          px: 2,
          backgroundColor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, val: number) => setActiveTab(val)}
          aria-label="Run stage tabs"
          sx={{ flex: 1 }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                  Stage 1
                </Typography>
                <Typography variant="body2">Plan</Typography>
              </Box>
            }
            id="run-tab-0"
            aria-controls="run-tabpanel-0"
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                  Stage 2
                </Typography>
                <Typography variant="body2">Execute</Typography>
              </Box>
            }
            id="run-tab-1"
            aria-controls="run-tabpanel-1"
          />
        </Tabs>

        <Tooltip title="Refresh">
          <IconButton size="small" onClick={handleRefresh} disabled={isSyncing} aria-label="Refresh">
            <RefreshOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Tab panels */}
      <TabPanel value={activeTab} index={0}>
        {context.plan ? (
          <PlanStage
            plan={context.plan}
            runId={context.run._id}
            runUid={context.run.uid}
            isReadOnly={context.run.status === 'ABORTED'}
          />
        ) : (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">Plan not yet created for this run.</Alert>
          </Box>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {context.plan && context.execute ? (
          <ExecuteStage
            plan={context.plan}
            execute={context.execute}
            run={context.run}
            isReadOnly={context.run.status === 'ABORTED'}
          />
        ) : (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">Execute stage not yet initialized for this run.</Alert>
          </Box>
        )}
      </TabPanel>
    </Box>
  );
};
